import { BaseClient } from './baseClient';
import { APIResponse } from '../types';
import {
    BOMStatusResponse,
    RunMarker,
} from '../types/bom.types';
import {
    BomLineOrigin,
    BomVersionDetail,
    BomVersionMeta,
    ManualEditRequest,
    ManualEditResult,
    SmartBomComponentEdit,
} from '../types/bomVersions';
import { DiscoveryStarted, SelectedLines, StartDiscoveryRequest } from '../types/discovery.types';
import { downloadBlob } from '../utils/download';

class BomClient extends BaseClient {
    constructor() {
        super();
    }

    /**
     * Leave step 2: charge the BOM for supplier discovery and start it.
     * `componentIds` keeps only those lines (the rest are removed from the BOM);
     * omitted, every line goes. A short balance rejects with 402
     * INSUFFICIENT_CREDITS — read it with `readDiscoveryRefusal`. Contract:
     * docs/flows/02-bom-edit-and-discovery-charge.md §3.
     */
    async analyzeBOM(rfqId: string, request: StartDiscoveryRequest = {}): Promise<DiscoveryStarted> {
        const response = await this.api.post<APIResponse<DiscoveryStarted>>('/trp/bom/analyze', {
            rfqId,
            ...request,
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to analyze BOM');
    }

    /**
     * Enter step 2 short of credits: keep only these lines, free of charge.
     * The BOM is trimmed and the dropped lines are versioned; discovery is
     * still charged at the exit, for the lines that remain. Selecting the
     * whole BOM is a no-op. A bad pick rejects with 400 (`UNKNOWN_COMPONENT`
     * names the line) — read it with `discoveryErrorCode`. Contract:
     * docs/flows/02-bom-edit-and-discovery-charge.md, "Entering step 2".
     */
    async selectBomLines(rfqId: string, componentIds: string[]): Promise<SelectedLines> {
        const response = await this.api.post<APIResponse<SelectedLines>>('/trp/bom/select', {
            rfqId,
            componentIds,
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to select BOM lines');
    }

    async startSupplierResearch(
        bomId: string,
        payload: { components: any[], requirements: any, contextAnswers: any, ruleResults: any[], aiSuggestion: any }
    ): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/research`, payload);

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to start supplier research');
    }

    async getBOMStatus(bomId: string): Promise<BOMStatusResponse> {
        const response = await this.api.get<APIResponse<BOMStatusResponse>>(`/trp/bom/${bomId}/status`);

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to get BOM status');
    }

    async exportToExcel(bomId: string): Promise<void> {
        try {
            const response = await this.api.get(`/trp/bom/${bomId}/export`, {
                responseType: 'blob'
            });

            // Create a blob with the correct MIME type
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create a download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `BOM_${bomId}_${Date.now()}.xlsx`);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            setTimeout(() => {
                link.remove();
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Failed to export BOM to Excel');
        }
    }
    async retryComponentResearch(bomId: string, componentId: string): Promise<void> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/components/${componentId}/retry`);

        if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to retry component research');
        }
    }

    // Robotics Intelligence APIs
    async getAnalysisStatus(bomId: string): Promise<any> {
        const response = await this.api.get<APIResponse<any>>(`/trp/bom/${bomId}/analysis`);

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to get analysis status');
    }

    /**
     * Persist per-finding review verdicts (merge semantics; null deletes a
     * key). The server clears the whole map on every validation re-run.
     */
    async saveAnalysisReviewState(
        bomId: string,
        states: Record<string, 'applied' | 'skipped' | null>
    ): Promise<Record<string, 'applied' | 'skipped'>> {
        const response = await this.api.put<APIResponse<any>>(`/trp/bom/${bomId}/analysis/review-state`, { states });
        if (response.data.success && response.data.data) {
            return response.data.data.reviewStates;
        }
        throw new Error(response.data.error || 'Failed to save review state');
    }

    async saveContextAnswers(bomId: string, contextAnswers: Record<string, any>): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/context`, {
            contextAnswers
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to save context answers');
    }

    async runValidationRules(bomId: string): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/run-checks`);

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to run validation rules');
    }

    async generateAIRecommendation(bomId: string): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/ai-recommendation`);

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to generate AI recommendation');
    }

    async saveAIDecision(bomId: string, decision: 'accepted' | 'rejected'): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/ai-decision`, {
            decision
        });

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to save AI decision');
    }

    // Merged LLM validation + recommendation (replaces runValidationRules + generateAIRecommendation)
    async runValidationAndRecommendation(bomId: string): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(`/trp/bom/${bomId}/validate-and-recommend`);

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to run validation and recommendation');
    }

    /** Starts the analysis again after a failed run; idempotent while one is running. */
    async retryAnalysis(bomId: string): Promise<{ started: boolean; run: RunMarker | null }> {
        const response = await this.api.post<APIResponse<{ started: boolean; run: RunMarker | null }>>(
            `/trp/bom/${bomId}/analysis-runs`
        );
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || "I couldn't restart the analysis of this BOM.");
    }

    // Replace a BOM component with an alternative suggested by the LLM
    async replaceComponent(bomId: string, componentIndex: number, alternative: { name: string; reason?: string }): Promise<any> {
        const response = await this.api.patch<APIResponse<any>>(
            `/trp/bom/${bomId}/components/${componentIndex}/replace`,
            alternative
        );

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to replace component');
    }

    // Revert a BOM component to a previous version in its replacementHistory
    async revertComponent(bomId: string, componentIndex: number, targetVersionIndex: number): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(
            `/trp/bom/${bomId}/components/${componentIndex}/revert`,
            { targetVersionIndex }
        );

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to revert component');
    }

    // Ask a contextual follow-up question about a specific AI suggestion
    async askSuggestionFeedback(
        bomId: string,
        suggestionKey: string,
        question: string,
        suggestionContext: Record<string, any>
    ): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(
            `/trp/bom/${bomId}/suggestion-feedback`,
            { suggestionKey, question, suggestionContext }
        );

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to get feedback answer');
    }

    // Add a component to the BOM
    async addComponent(
        bomId: string,
        component: { name: string; specifications?: string; quantity?: number; manufacturer?: string; origin?: BomLineOrigin }
    ): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(
            `/trp/bom/${bomId}/components/add`,
            component
        );

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to add component');
    }

    // Manually set the contact email on a supplier candidate (persists to the BOM)
    async updateSupplierContactEmail(
        bomId: string,
        componentId: string,
        supplierName: string,
        email: string
    ): Promise<{ supplierName: string; hasContactEmail: boolean; contactSource: string }> {
        const response = await this.api.patch<APIResponse<{ supplierName: string; hasContactEmail: boolean; contactSource: string }>>(
            `/trp/supplier-contacts/bom/${bomId}/components/${componentId}/email`,
            { supplierName, email }
        );

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error((response.data as any).message || response.data.error || 'Failed to save contact email');
    }

    // ---- Smart BOM versions ------------------------------------------------

    private unwrap<T>(payload: APIResponse<T>, fallback: string): T {
        if (payload.success && payload.data !== undefined) return payload.data;
        throw new Error(payload.error || fallback);
    }

    /** Version metadata only (v1 "Extracted" → latest). */
    async listBomVersions(bomId: string): Promise<BomVersionMeta[]> {
        const res = await this.api.get<APIResponse<{ bomId: string; versions: BomVersionMeta[] }>>(`/trp/bom/${bomId}/versions`);
        return this.unwrap(res.data, 'Failed to load BOM versions').versions;
    }

    /** One version: snapshot + changes + reasoning rows up to that version. */
    async getBomVersion(bomId: string, version: number): Promise<BomVersionDetail> {
        const res = await this.api.get<APIResponse<BomVersionDetail>>(`/trp/bom/${bomId}/versions/${version}`);
        return this.unwrap(res.data, 'Failed to load BOM version');
    }

    /**
     * Manual edit — field edits, added lines and removed lines on the live
     * BOM, captured as ONE MANUAL_EDIT version. Pass a bare edit list (the
     * Smart BOM sheet) or the full request (the step-2 extracted-BOM sheet).
     * A 400 rejects with the axios error: `response.data.error` says why and
     * `response.data.componentId` names the line when one is to blame.
     */
    async manualEditBom(
        bomId: string,
        edit: SmartBomComponentEdit[] | ManualEditRequest
    ): Promise<ManualEditResult> {
        const body: ManualEditRequest = Array.isArray(edit) ? { components: edit } : edit;
        const res = await this.api.post<APIResponse<ManualEditResult>>(`/trp/bom/${bomId}/versions/manual-edit`, body);
        return this.unwrap(res.data, 'Failed to save BOM edits');
    }

    /** Download one version as .xlsx (Smart BOM sheet + Reasoning sheet). */
    async exportBomVersion(bomId: string, version: number): Promise<void> {
        const res = await this.api.get(`/trp/bom/${bomId}/versions/${version}/export`, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        downloadBlob(blob, `SmartBOM_${bomId}_v${version}.xlsx`);
    }

    // Save shortlisted suppliers across BOM components
    async shortlistSuppliersBatch(
        bomId: string,
        // source 'robbie' = the buyer applied Robbie's picks; the version is credited to Robbie.
        // `listings`: one id per ticked product row; when sent, the server uses it over `suppliers`.
        payload: { components: Array<{ componentId: string; suppliers: string[]; listings?: string[] }>; source?: 'buyer' | 'robbie' }
    ): Promise<any> {
        const response = await this.api.post<APIResponse<any>>(
            `/trp/bom/${bomId}/suppliers/shortlist`,
            payload
        );

        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to update shortlisted suppliers');
    }

    /**
     * "Use this instead": switch a line to an alternative spec discovery found
     * (`specGroup` as the read returned it). A BOM edit — recorded as the
     * buyer's change — that clears the old spec's shortlist.
     */
    async swapComponentSpec(bomId: string, componentId: string, specGroup: string): Promise<SpecSwapResult> {
        const res = await this.api.post<APIResponse<SpecSwapResult>>(
            `/trp/bom/${bomId}/components/${encodeURIComponent(componentId)}/spec-swap`,
            { specGroup }
        );
        return this.unwrap(res.data, 'Failed to switch the line to that spec');
    }
}

export interface SpecSwapResult {
    version: number | null;
    shortlistVersion: number | null;
    changes: Array<{ key: string; label: string; from: string; to: string }>;
}

export const bomClient = new BomClient();
